/* eslint-disable no-param-reassign */
import {
  useCurrentCompanyBankDetails,
  useDeleteCompanyBankDetails,
  useSetRepairersActivity,
} from 'app/core/api/company/api-company';
import {
  CompanyBankDetails,
  PageDetails,
} from 'app/core/api/company/api-company.types';
import Button from 'app/shared/components/button/Button';
import FormTextInput from 'app/shared/components/form-input/FormTextInput';
import useInputForm from 'app/shared/components/form-input/utilities/use-input-form';
import styled from 'styled-components/macro';
import ClassName from 'app/styles/ClassName';
import styledConsts from 'app/styles/styledConsts';
import styledFuncs from 'app/styles/styledFuncs';
// import { ErrorText } from 'app/shared/components/misc';
import Body from 'app/shared/components/typography/body/Body';
// import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import { useSetPageTitle } from 'app/shared/context-hooks/use-titlebar.hook';
import Form from 'app/shared/frames/Form';
import { useEffectOnce } from 'app/tools/hooks/misc-hooks';
import _ from 'lodash';
import { useObservableEagerState } from 'observable-hooks';
import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { BehaviorSubject } from 'rxjs';
import BusinessRoutePath from 'app/business-app/BusinessRoutePath';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import { getErrorMsg } from 'app/core/errors/error-utils';
// import { tap } from 'rxjs/operators';

const CompactButton = styled(Button).attrs({
  slim: true,
  className: ClassName.NO_HEIGHT_MARGIN,
})`
  margin-top: ${styledFuncs.pad()};
  margin-bottom: ${styledFuncs.pad(0.5)};
`;
const DeleteButton = styled(CompactButton).attrs({ variant: 'text' })`
  &,
  &:hover {
    color: white;
    background-color: ${styledConsts.cError};
  }
`;
const CenteredBox = styled.div`
  flex-grow: 1;
  flex-direction: column;
  display: flex;
  justify-content: center;
  text-align: center;
  > * {
    margin: ${styledFuncs.pad(0.5)} 0;
  }
`;
export default function BankDetails(): JSX.Element {
  useSetPageTitle('Business Bank Details');
  const history = useHistory();

  const { request$: repairActivity } = useSetRepairersActivity();
  // eslint-disable-next-line @typescript-eslint/unbound-method
  useEffectOnce(() => {
    const pageDetails: PageDetails = {
      pagename: 'Business Bank Details',
    };
    repairActivity(pageDetails).subscribe();
  });

  const { resource: company } = useCurrentCompanyBankDetails({
    fresh: true,
  });
  //   const { showSnack } = useShowInfo();

  const editable$ = useMemo(() => new BehaviorSubject(false), []);
  const editable = useObservableEagerState(editable$);

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { handleSubmit, inputProps, formState } =
    useInputForm<CompanyBankDetails>({
      mode: 'onTouched',
      defaultValues: company,
    });

  const { request$: deleteAssignedAccount$ } =
    useDeleteCompanyBankDetails();

  const { showDialog, showSnack } = useShowInfo();

  const handleDeleteSuccess = () => {
    showSnack({
      message: 'Bank Account deleted',
      alertType: 'success',
    });
    history.replace(BusinessRoutePath.PAYMENT);
  };

  const handleDeleteError = (e: unknown) => {
    showSnack({ message: getErrorMsg(e), alertType: 'error' });
  };

  const deleteConfirmCallback = () =>
    deleteAssignedAccount$().subscribe({
      next: handleDeleteSuccess,
      error: handleDeleteError,
    });
  const handleDeleteRequest = () =>
    showDialog({
      title: 'Delete assigned product',
      message: 'Are you sure you want to delete your bank account',
      actions: [
        { text: 'Cancel' },
        {
          text: 'Confirm Delete',
          callback: () => {
            deleteConfirmCallback();
          },
        },
      ],
    });
  const noBankAccount = (
    <CenteredBox>
      <text>
        Nothing to see here yet - to see your bank account details
        return to the payment page and add your payment method
      </text>
    </CenteredBox>
  );
  const foundBankAccount = (
    <>
      <Form
        flexStretch
        onSubmit={handleSubmit(details => {
          if (_.isEmpty(formState.touched)) {
            history.goBack();
            return;
          }
          details.accountholdertype = 'individual';
          details.currency = 'gbp';
        })}
      >
        <Body color="primary" style={{ fontWeight: 'normal' }}>
          Business Bank Details
        </Body>
        <FormTextInput
          controlName="accountholdername"
          placeholder="Account Holder Name"
          autoComplete="accountholdername"
          disabled={!editable}
          {...inputProps}
        />
        <FormTextInput
          controlName="sortcode"
          placeholder="Sort Code"
          autoComplete="sortcode"
          disabled={!editable}
          {...inputProps}
        />
        <FormTextInput
          controlName="accountnumber"
          autoComplete="accountnumber"
          placeholder="Account Number"
          disabled={!editable}
          {...inputProps}
        />
        <FormTextInput
          controlName="confirmaccountnumber"
          autoComplete="confirmaccountnumber"
          placeholder="Confirm Account Number"
          disabled={!editable}
          {...inputProps}
        />
        <FormTextInput
          controlName="bankname"
          placeholder="Bank Name"
          disabled={!editable}
          {...inputProps}
        />
        <DeleteButton onClick={handleDeleteRequest}>
          Delete
        </DeleteButton>
      </Form>
    </>
  );
  return <>{company ? foundBankAccount : noBankAccount}</>;
}
